import { useEffect, useRef, useState } from 'react';

/** What we pull out of a suggestion, in the shape the checkout form uses. */
export interface ResolvedAddress {
  line1: string;
  suburb: string;
  city: string;
  postalCode: string;
}

interface Props {
  value: string;
  /** Kept for call-site compatibility; Photon needs no key. */
  apiKey?: string | null;
  /** Suburbs the shop actually delivers to, used to match the suggestion. */
  knownSuburbs: string[];
  onTextChange: (value: string) => void;
  onResolved: (address: ResolvedAddress, matchedSuburb: string | null) => void;
}

interface Suggestion {
  label: string;
  address: ResolvedAddress;
}

/** Randburg Square, so suggestions near the shop come first. */
const SHOP = { lat: -26.0936, lon: 27.9975 };

/**
 * Street address field with type-ahead from Photon (Komoot's OpenStreetMap
 * geocoder).
 *
 * Chosen over Google Places because it needs no API key, no billing account
 * and no card on file, and it returns South African streets perfectly well.
 * The trade-off is honest: OSM's coverage of new estates is patchier than
 * Google's, so the field stays free text — a customer can always type an
 * address the map has never heard of and still order.
 *
 * The suburb is matched against the shop's own delivery zones rather than
 * trusted. OSM's idea of a suburb boundary is not the price list.
 */
export function AddressAutocomplete({ value, knownSuburbs, onTextChange, onResolved }: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  // Set while applying a suggestion, so the resulting value change does not
  // immediately trigger another search for what was just chosen.
  const applying = useRef(false);

  useEffect(() => {
    if (applying.current) {
      applying.current = false;
      return;
    }

    const query = value.trim();
    if (query.length < 4) {
      setSuggestions([]);
      return;
    }

    // Debounced: one request per pause in typing, not per keystroke. Photon is
    // a donated service and this is a shop, not a search engine.
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);

      try {
        const url =
          `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}` +
          `&limit=5&lang=en&lat=${SHOP.lat}&lon=${SHOP.lon}`;

        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('lookup failed');

        const data = (await response.json()) as PhotonResponse;

        setSuggestions(
          (data.features ?? [])
            .filter((f) => f.properties?.countrycode === 'ZA')
            .map(toSuggestion)
            .filter((s) => s.address.line1.length > 0),
        );
        setOpen(true);
      } catch {
        // A failed lookup is not an error the customer needs to see — they can
        // type the address themselves, which is what the field already is.
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [value]);

  useEffect(() => {
    function onDocumentClick(event: MouseEvent) {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    }

    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  function choose(suggestion: Suggestion) {
    applying.current = true;
    setOpen(false);
    setSuggestions([]);
    onResolved(suggestion.address, matchSuburb(suggestion.address, knownSuburbs));
  }

  return (
    <div className="addr" ref={boxRef}>
      <input
        value={value}
        autoComplete="off"
        placeholder="Start typing your street address…"
        onChange={(e) => onTextChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        // Enter would otherwise submit the form underneath the open list.
        onKeyDown={(e) => {
          if (e.key === 'Enter' && open) e.preventDefault();
          if (e.key === 'Escape') setOpen(false);
        }}
      />

      {loading && <span className="muted small">Looking up addresses…</span>}

      {open && suggestions.length > 0 && (
        <ul className="addr__list">
          {suggestions.map((s, i) => (
            <li key={`${s.label}-${i}`}>
              <button type="button" onClick={() => choose(s)}>
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function toSuggestion(feature: PhotonFeature): Suggestion {
  const p = feature.properties ?? {};

  const line1 = [p.housenumber, p.street ?? p.name].filter(Boolean).join(' ').trim();

  // Photon puts the suburb in different fields depending on how the area was
  // mapped, and its "district" is sometimes a ward number, which is no use.
  const suburb = [p.suburb, p.district, p.locality]
    .find((v) => v && !/ward\s*\d+/i.test(v)) ?? '';

  const address: ResolvedAddress = {
    line1: line1 || p.name || '',
    suburb: suburb || '',
    city: p.city ?? p.county ?? '',
    postalCode: p.postcode ?? '',
  };

  const label = [address.line1, address.suburb, address.city].filter(Boolean).join(', ');

  return { label: label || address.line1, address };
}

/**
 * Matches the suggestion against the shop's price list, case- and
 * whitespace-insensitively. Null means "ask the customer to pick", because
 * guessing would quote the fee for the wrong zone.
 */
function matchSuburb(address: ResolvedAddress, known: string[]): string | null {
  const normalise = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  const candidates = [address.suburb, address.city].filter(Boolean).map(normalise);

  return known.find((k) => candidates.includes(normalise(k))) ?? null;
}

interface PhotonProperties {
  name?: string;
  street?: string;
  housenumber?: string;
  suburb?: string;
  district?: string;
  locality?: string;
  city?: string;
  county?: string;
  postcode?: string;
  countrycode?: string;
}

interface PhotonFeature {
  properties?: PhotonProperties;
}

interface PhotonResponse {
  features?: PhotonFeature[];
}
